// Journal des livraisons de webhooks n8n — stats 24h, filtres, détail, rejeu.
import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { RefreshCw, RotateCcw, CheckCircle2, AlertTriangle, Timer, Activity } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import api, { apiError } from "@/lib/api";
import { EVENEMENTS_WEBHOOK } from "@/lib/constants";

const LIBELLE_STATUT = {
  success: "Succès",
  failed_client: "Refus (4xx)",
  failed_server: "Erreur serveur (5xx)",
  failed_network: "Réseau / timeout",
};

const CLASSE_STATUT = {
  success: "bg-emerald-500/15 text-emerald-700 dark:text-emerald-400 border-emerald-500/30",
  failed_client: "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
  failed_server: "bg-destructive/15 text-destructive border-destructive/30",
  failed_network: "bg-destructive/15 text-destructive border-destructive/30",
};

const Carte = ({ icone: Icone, libelle, valeur, suffixe }) => (
  <div className="bg-card rounded-2xl border border-border p-5 carte-douce">
    <div className="flex items-center gap-2 text-muted-foreground">
      <Icone className="h-4 w-4" />
      <span className="text-xs uppercase tracking-wide">{libelle}</span>
    </div>
    <p className="font-serif text-3xl font-semibold mt-2">
      {valeur}
      {suffixe && <span className="text-base text-muted-foreground ml-1">{suffixe}</span>}
    </p>
  </div>
);

const formatHorodatage = (iso) => {
  if (!iso) return "—";
  return new Date(iso).toLocaleString("fr-BE", {
    day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit", second: "2-digit",
  });
};

const joliJson = (texte) => {
  if (!texte) return "—";
  try { return JSON.stringify(JSON.parse(texte), null, 2); } catch { return texte; }
};

export const OngletJournalWebhooks = () => {
  const [stats, setStats] = useState(null);
  const [lignes, setLignes] = useState([]);
  const [event, setEvent] = useState("tous");
  const [statut, setStatut] = useState("tous");
  const [since, setSince] = useState("7d");
  const [chargement, setChargement] = useState(false);
  const [detail, setDetail] = useState(null);

  const charger = useCallback(async () => {
    setChargement(true);
    try {
      const params = { since };
      if (event !== "tous") params.event = event;
      if (statut !== "tous") params.statut = statut;
      const [s, l] = await Promise.all([
        api.get("/webhook-deliveries/stats"),
        api.get("/webhook-deliveries", { params }),
      ]);
      setStats(s.data);
      setLignes(l.data);
    } catch (e) {
      toast.error(apiError(e));
    } finally {
      setChargement(false);
    }
  }, [event, statut, since]);

  useEffect(() => { charger(); }, [charger]);

  const rejouer = async (id) => {
    try {
      const { data } = await api.post(`/webhook-deliveries/${id}/replay`);
      toast.success(data.message);
      setTimeout(charger, 4000);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const echecs24h = stats ? stats.total_24h - stats.success_24h : 0;

  return (
    <div className="space-y-6" data-testid="webhook-logs-tab">
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        <Carte icone={Activity} libelle="Envois 24 h" valeur={stats?.total_24h ?? "—"} />
        <Carte icone={CheckCircle2} libelle="Taux de succès" valeur={stats?.taux_succes_24h ?? "—"} suffixe={stats?.taux_succes_24h != null ? "%" : ""} />
        <Carte icone={AlertTriangle} libelle="Échecs 24 h" valeur={stats ? echecs24h : "—"} />
        <Carte icone={Timer} libelle="Latence moyenne" valeur={stats?.avg_latency_ms_24h ?? "—"} suffixe="ms" />
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <Select value={event} onValueChange={setEvent}>
          <SelectTrigger className="rounded-full w-[230px]" data-testid="webhook-logs-filter-event"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous les événements</SelectItem>
            {EVENEMENTS_WEBHOOK.map((ev) => <SelectItem key={ev} value={ev}>{ev}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={statut} onValueChange={setStatut}>
          <SelectTrigger className="rounded-full w-[190px]" data-testid="webhook-logs-filter-statut"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="tous">Tous les statuts</SelectItem>
            {Object.entries(LIBELLE_STATUT).map(([k, v]) => <SelectItem key={k} value={k}>{v}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={since} onValueChange={setSince}>
          <SelectTrigger className="rounded-full w-[150px]" data-testid="webhook-logs-filter-since"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="24h">24 dernières h</SelectItem>
            <SelectItem value="7d">7 derniers jours</SelectItem>
            <SelectItem value="30d">30 derniers jours</SelectItem>
          </SelectContent>
        </Select>
        <Button variant="outline" onClick={charger} className="rounded-full gap-2" data-testid="webhook-logs-refresh">
          <RefreshCw className={`h-4 w-4 ${chargement ? "animate-spin" : ""}`} /> Rafraîchir
        </Button>
      </div>

      <div className="space-y-2" data-testid="webhook-logs-list">
        {lignes.map((l) => (
          <div key={l.id} className="flex flex-wrap items-center gap-3 bg-card rounded-2xl border border-border px-5 py-3.5 carte-douce" data-testid={`webhook-log-row-${l.id}`}>
            <Badge variant="outline" className={`text-[10px] ${CLASSE_STATUT[l.statut] || ""}`}>
              {LIBELLE_STATUT[l.statut] || l.statut}
            </Badge>
            <div className="flex-1 min-w-[180px]">
              <p className="text-sm font-medium font-mono truncate">{l.event}</p>
              <p className="text-xs text-muted-foreground">
                {formatHorodatage(l.created_at)} · tentative {l.tentative} · {l.latency_ms} ms
                {l.status_code ? ` · HTTP ${l.status_code}` : ""}
                {l.erreur ? ` · ${l.erreur}` : ""}
              </p>
            </div>
            <Button size="sm" variant="ghost" onClick={() => setDetail(l)} className="rounded-full text-xs" data-testid={`webhook-log-detail-${l.id}`}>
              Détail
            </Button>
            {l.statut !== "success" && (
              <Button size="sm" variant="ghost" onClick={() => rejouer(l.id)} className="rounded-full gap-1.5 text-xs" data-testid={`webhook-log-replay-${l.id}`}>
                <RotateCcw className="h-3.5 w-3.5" /> Rejouer
              </Button>
            )}
          </div>
        ))}
        {lignes.length === 0 && !chargement && (
          <p className="text-sm text-muted-foreground text-center py-8" data-testid="webhook-logs-empty">
            Aucune livraison sur cette période.
          </p>
        )}
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-w-2xl" data-testid="webhook-log-detail-dialog">
          <DialogHeader>
            <DialogTitle className="font-serif">{detail?.event}</DialogTitle>
            <DialogDescription>
              {detail && `${formatHorodatage(detail.created_at)} — ${LIBELLE_STATUT[detail.statut] || detail.statut}`}
            </DialogDescription>
          </DialogHeader>
          {detail && (
            <div className="space-y-4 max-h-[60vh] overflow-y-auto">
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Destination</p>
                <p className="text-xs font-mono break-all">{detail.url}</p>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Payload envoyé ({detail.payload_size} o)</p>
                <pre className="text-[11px] bg-muted rounded-xl p-3 overflow-x-auto whitespace-pre-wrap" data-testid="webhook-log-payload">{joliJson(detail.payload_snippet)}</pre>
              </div>
              <div>
                <p className="text-xs uppercase tracking-wide text-muted-foreground mb-1">Réponse n8n {detail.status_code ? `(HTTP ${detail.status_code})` : ""}</p>
                <pre className="text-[11px] bg-muted rounded-xl p-3 overflow-x-auto whitespace-pre-wrap" data-testid="webhook-log-response">{detail.erreur || joliJson(detail.response_snippet)}</pre>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};
